import MapsController from "../../../../../app/javascript/controllers/maps_controller.js"
import Sortable from "sortablejs"
import { numberedMapMarker } from "../../../../../app/javascript/utils/numbered_map_marker.js"

export default class extends MapsController {
  static targets = [...MapsController.targets, "search", "listStatus", "sortStatus", "saveStatus", "status", "layout"]
  static values = { ...MapsController.values, runOptions: Array, sortable: Boolean, readonly: Boolean }

  connect() {
    this.allMarkers = this.markersValue
    this.handleMapUpdate = this.handleMapUpdate.bind(this)
    this.handleListSelection = this.handleListSelection.bind(this)
    this.handleMapError = () => this.mapUnavailable()
    window.addEventListener("delivery:client-runs-map-update", this.handleMapUpdate)
    window.addEventListener("delivery:client-runs-list-select", this.handleListSelection)
    window.addEventListener("google-maps:error", this.handleMapError)
    this.loadTimeout = setTimeout(() => { if (!this.mapInitialized && this.markersValue.length) this.mapUnavailable() }, 12000)
    this.setupSorting()
    this.filterLocations()
    super.connect()
    if (window.googleMapsAuthFailed) this.mapUnavailable()
  }

  disconnect() {
    window.removeEventListener("delivery:client-runs-map-update", this.handleMapUpdate)
    window.removeEventListener("delivery:client-runs-list-select", this.handleListSelection)
    window.removeEventListener("google-maps:error", this.handleMapError)
    clearTimeout(this.loadTimeout)
    clearTimeout(this.searchTimeout)
    this.searchRequest?.abort()
    this.sortable?.destroy()
    super.disconnect()
  }

  initializeMap() {
    if (!this.markersValue.length || window.googleMapsAuthFailed) return
    try {
      super.initializeMap()
      if (!this.mapInitialized) return
      clearTimeout(this.loadTimeout)
      this.mapFailed = false
      this.mapTarget.hidden = false
      this.map().setOptions({ mapTypeControl: false, streetViewControl: false })
      this.fitLocations()
      this.updateMapStatus()
    } catch (_error) {
      this.mapUnavailable()
    }
  }

  mapUnavailable() {
    this.mapFailed = true
    this.mapTarget.hidden = true
    this.statusTarget.textContent = "Map unavailable. Use the location list."
  }

  updateMapStatus() {
    if (this.mapFailed) return
    const mapped = this.markersValue.length
    const visible = this.locationRows().filter(row => !row.hidden).length
    this.mapTarget.hidden = !mapped
    this.statusTarget.textContent = mapped ? (this.mapInitialized ? `${mapped} mapped${visible > mapped ? ` · ${visible - mapped} without pins` : ""}` : "Loading map…") : "No map pins. Use the location list."
  }

  locationRows() { return [...this.element.querySelectorAll(".client-runs-list-item")] }
  locationList() { return this.element.querySelector("#client_runs_locations_list") }

  searchChanged() {
    this.filterLocations()
    this.fitLocations()
    clearTimeout(this.searchTimeout)
    this.searchRequest?.abort()
    this.searchTimeout = setTimeout(() => this.searchLocations(), 250)
  }

  async searchLocations(event) {
    event?.preventDefault()
    clearTimeout(this.searchTimeout)
    this.searchRequest?.abort()
    if (this.busy) { this.searchPending = true; return }
    this.searchPending = false
    const request = new AbortController()
    this.searchRequest = request
    const form = this.searchTarget.form
    const url = new URL(form.action)
    url.search = new URLSearchParams(new FormData(form)).toString()
    try {
      const response = await fetch(url, { signal: request.signal, headers: { "Accept": "text/vnd.turbo-stream.html" } })
      if (!response.ok || !response.headers.get("Content-Type")?.includes("text/vnd.turbo-stream.html")) throw new Error("Search failed")
      const html = await response.text()
      if (request.signal.aborted || !this.element.isConnected) return
      window.Turbo.renderStreamMessage(html)
      await new Promise(resolve => requestAnimationFrame(resolve))
      this.fitLocations()
    } catch (error) {
      if (error.name !== "AbortError" && this.element.isConnected) this.listStatusTarget.textContent = "Could not search. Please retry."
    } finally {
      if (this.searchRequest === request) this.searchRequest = null
    }
  }

  filterLocations() {
    const query = this.searchTarget.value.trim().toLocaleLowerCase()
    const rows = this.locationRows()
    rows.forEach(row => {
      row.hidden = !row.dataset.clientRunsSearch.toLocaleLowerCase().includes(query)
      row.querySelectorAll(".handle").forEach(handle => { handle.hidden = !!query })
    })
    const visible = new Set(rows.filter(row => !row.hidden).map(row => row.id))
    this.element.querySelector("[data-client-runs-empty]").hidden = visible.size > 0
    this.listStatusTarget.textContent = query ? `${visible.size} of ${rows.length} locations` : `${rows.length} locations`
    this.sortStatusTarget.textContent = this.readonlyValue ? "View only" : (this.sortableValue ? (query ? "Clear search to reorder stops" : "Drag locations to order · saves automatically") : "Changes save automatically · choose a run to sort")
    this.sortable?.option("disabled", this.busy || !!query)
    const offset = Number(this.element.querySelector("[data-client-runs-locations]")?.dataset.offset || 0)
    const positions = new Map(rows.map((row, index) => [row.id, index + 1 + offset]))
    this.markersValue = this.allMarkers.filter(marker => visible.has(marker.list_item_id)).map(marker => ({
      ...marker, stop_number: this.sortableValue ? (marker.stop_number || positions.get(marker.list_item_id)) : null
    }))
    if (this.mapInitialized && !this.mapFailed) this.applyMarkerUpdate(this.markersValue, this.centerValue)
    else if (window.google?.maps?.Map && !this.mapFailed) this.initializeMap()
    this.updateMapStatus()
    this.applySelectedListItemHighlight()
  }

  handleMapUpdate(event) {
    this.allMarkers = event.detail.markers || []
    this.centerValue = event.detail.center || this.centerValue
    this.setupSorting()
    this.filterLocations()
  }

  setupSorting() {
    this.sortable?.destroy()
    this.sortable = null
    if (!this.sortableValue || this.readonlyValue) return
    this.sortable = Sortable.create(this.element.querySelector("[data-client-runs-locations]"), {
      handle: ".handle", draggable: ".client-runs-list-item", animation: 150,
      delay: 150, delayOnTouchOnly: true, ghostClass: "shipment-runs-drag-ghost",
      disabled: this.busy || !!this.searchTarget.value.trim(),
      onEnd: async ({ item, oldIndex, newIndex }) => {
        if (oldIndex === newIndex) return
        const saved = await this.savePosition(item, newIndex)
        if (!saved && item.isConnected) {
          const rows = this.locationRows().filter(row => row !== item)
          item.parentElement.insertBefore(item, rows[oldIndex] || null)
        }
      }
    })
  }

  async moveWithKeyboard(event) {
    if (!["ArrowUp", "ArrowDown", "Home", "End"].includes(event.key) || this.busy || this.searchTarget.value.trim()) return
    event.preventDefault()
    const item = event.currentTarget.closest(".client-runs-list-item")
    const rows = this.locationRows()
    const index = rows.indexOf(item)
    const position = event.key === "Home" ? 0 : event.key === "End" ? rows.length - 1 : index + (event.key === "ArrowUp" ? -1 : 1)
    if (position < 0 || position >= rows.length || position === index) return
    const id = item.id
    await this.savePosition(item, position)
    this.element.querySelector(`#${id} .handle`)?.focus()
  }

  savePosition(item, index) {
    const data = new FormData()
    data.append("position", index + 1)
    return this.saveChange(item.dataset.sortableUpdateUrl, "PATCH", data)
  }

  async assignLocation(event) {
    const select = event.currentTarget
    const saved = await this.saveChange(this.buildAssignmentUrl(select.dataset.assignUrl, select.value), "POST")
    if (!saved) select.value = select.dataset.savedRun || ""
  }

  async saveChange(url, method, body) {
    if (this.readonlyValue || this.busy) return false
    clearTimeout(this.searchTimeout)
    this.searchRequest?.abort()
    // Refresh the saved rows with the current query, even before its debounce fires.
    const changeUrl = new URL(url, window.location.origin)
    const query = this.searchTarget.value.trim()
    if ((changeUrl.searchParams.get("q") || "") !== query) changeUrl.searchParams.delete("page")
    changeUrl.searchParams.set("q", query)
    this.busy = true
    this.saveStatusTarget.textContent = "Saving…"
    this.saveStatusTarget.classList.remove("fg-danger")
    this.setControlsDisabled(true)
    try {
      const response = await fetch(changeUrl, { method, body, headers: {
        "Accept": "text/vnd.turbo-stream.html",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
      } })
      if (!response.ok || !response.headers.get("Content-Type")?.includes("text/vnd.turbo-stream.html")) throw new Error("Save failed")
      const html = await response.text()
      if (!this.element.isConnected) return true
      window.Turbo.renderStreamMessage(html)
      await new Promise(resolve => requestAnimationFrame(resolve))
      this.saveStatusTarget.textContent = "Saved"
      return true
    } catch (_error) {
      this.saveStatusTarget.textContent = "Could not save. Please retry."
      this.saveStatusTarget.classList.add("fg-danger")
      return false
    } finally {
      this.busy = false
      this.setControlsDisabled(false)
      if (this.searchPending && this.element.isConnected) this.searchLocations()
    }
  }

  setControlsDisabled(disabled) {
    this.element.querySelectorAll("select[data-assign-url], .handle, .client-runs-popup-select").forEach(control => { control.disabled = disabled })
    this.sortable?.option("disabled", disabled || !!this.searchTarget.value.trim())
  }

  handleListSelection(event) {
    const listItemId = event.detail?.listItemId
    const entry = this.markerLookup?.get(listItemId)
    if (!entry || this.mapFailed || this.mapTarget.hidden) { this.focusLinkedListItem(listItemId); return }
    this.showView("map")
    requestAnimationFrame(() => this.openMarker(entry.marker, entry.markerData))
  }

  markerClicked(markerData) { this.focusLinkedListItem(markerData.list_item_id) }

  markerOptionsFor(markerData) {
    if (!markerData.stop_number) return super.markerOptionsFor(markerData)
    return { ...numberedMapMarker(markerData.color, [markerData.stop_number]), title: `Stop ${markerData.stop_number} · ${markerData.title}` }
  }

  buildAssignmentUrl(basePath, runId) {
    const url = new URL(basePath, window.location.origin)
    url.searchParams.set("default_run_id", runId)
    return url.toString()
  }

  buildInfoWindowContent(markerData) {
    const wrapper = document.createElement("div")
    wrapper.style.cssText = "min-width:15rem;max-width:20rem"
    const header = document.createElement("div")
    header.className = "d-flex align-items-start justify-content-between gap-2 mb-2"
    const title = document.createElement("strong")
    title.textContent = markerData.title
    const close = document.createElement("button")
    close.type = "button"
    close.className = "btn btn-sm btn-link p-0"
    close.textContent = "Close"
    close.addEventListener("click", () => this.infoWindow().close())
    header.append(title, close)
    wrapper.append(header)
    if (markerData.stop_number) {
      const number = document.createElement("div")
      number.className = "small fw-semibold mb-2"
      number.textContent = `Stop ${markerData.stop_number}`
      wrapper.append(number)
    }
    const address = document.createElement("div")
    address.className = "small fg-2 mb-2"
    address.textContent = markerData.address
    wrapper.append(address)
    if (this.readonlyValue) {
      const run = document.createElement("div")
      run.textContent = markerData.run_name
      wrapper.append(run)
    } else {
      const select = document.createElement("select")
      select.className = "form-control form-control-sm client-runs-popup-select"
      select.setAttribute("aria-label", `Map run for ${markerData.title}`)
      select.disabled = !!this.busy
      const current = markerData.assigned_run_id?.toString() || ""
      ;[{ id: "", label: "Unassigned" }, ...this.runOptionsValue].forEach(run => select.add(new Option(run.label, run.id)))
      select.value = current
      select.addEventListener("change", async () => {
        const saved = await this.saveChange(this.buildAssignmentUrl(markerData.assign_path, select.value), "POST")
        if (saved) this.infoWindow().close()
        else select.value = current
      })
      wrapper.append(select)
    }
    return wrapper
  }

  openMarker(marker, markerData) {
    this.map().panTo(marker.getPosition())
    super.openMarker(marker, markerData)
  }

  focusLinkedListItem(listItemId) {
    this.selectedListItemId = listItemId
    const item = this.applySelectedListItemHighlight()
    const list = this.locationList()
    if (item && !item.hidden && list.clientHeight) {
      const top = item.getBoundingClientRect().top - list.getBoundingClientRect().top + list.scrollTop
      list.scrollTo({ top: top - list.clientHeight / 2 + item.clientHeight / 2, behavior: "smooth" })
    }
  }

  applySelectedListItemHighlight() {
    const rows = this.locationRows()
    rows.forEach(row => row.classList.toggle("bg-subtle-primary", row.id === this.selectedListItemId))
    return rows.find(row => row.id === this.selectedListItemId)
  }

  switchView(event) { this.showView(event.currentTarget.dataset.view) }
  showView(view) {
    this.layoutTarget.dataset.mobileView = view
    this.element.querySelectorAll("[data-view]").forEach(button => {
      button.classList.toggle("active", button.dataset.view === view)
      button.setAttribute("aria-pressed", String(button.dataset.view === view))
    })
    if (view === "map") requestAnimationFrame(() => this.fitLocations())
    else this.focusLinkedListItem(this.selectedListItemId)
  }

  fitLocations() {
    if (!this._map || this.mapFailed || this.mapTarget.hidden || !this.mapTarget.clientWidth) return
    this.fittedVisible = true
    const bounds = new google.maps.LatLngBounds()
    this.markers().forEach(marker => bounds.extend(marker.getPosition()))
    if (this.markers().length === 1) { this.map().setCenter(bounds.getCenter()); this.map().setZoom(15) }
    else if (this.markers().length) this.map().fitBounds(bounds, 48)
  }

  afterResize() { if (!this.fittedVisible) this.fitLocations() }
}
